import React from 'react';
import * as _ from 'lodash';
import { STATUS_LIST, ATTRIBUTE_TYPE } from '../../../HelperMethods/enums';
import {
  LOCAL_STORAGE_TASK_LIST_FILTER_MODEL,
  LOCAL_STORAGE_TASK_LIST_TEMP_FILTER,
} from '../../../HelperMethods/AgGridConstants';
import { RESOLVED_VIA } from './ManageTaskForm/lib/constants';
import { parseJSONStringToObject } from '../../../HelperMethods/UtilityFunctions';
import { determineIfUserHasAccessToEngineSetting } from '../../../HelperMethods/UserUtilities';
import { RESOURCE_TYPE, RESOURCES } from '../../../HelperMethods/role-resource-constants';

export function mergeAttributesForBulkEditTasks(tasks, formValues, assignees, userName, acceptBulkAccept = false) {
  if (!acceptBulkAccept) {
    const tasksCopy = _.cloneDeep(tasks);
    const dateNow = new Date().toISOString();
    for (const task of tasksCopy) {
      const formAssigneeIds = (_.get(formValues, 's_assigneeid') || _.get(formValues, 'assigneeIds') || []).filter(
        (id) => !_.isEmpty(id)
      );
      if (formAssigneeIds?.length) {
        const newAssigneeIds = [];
        const existingAssigneeIds = task.assigneeIds.filter((assigneeIdObj) => !assigneeIdObj.isDeleted);
        const existingDeletedAssigneeIdObjects = task.assigneeIds.filter((assigneeIdObj) => assigneeIdObj.isDeleted);
        const assigneeIdKeyMap = _.keyBy(assignees, 'id');
        const assigneeObj = {};
        formAssigneeIds.forEach((assigneeId) => {
          const assignee = assigneeIdKeyMap[assigneeId];
          if (!assignee) return;
          const obj = {
            assigneeId: assignee.id,
            sequence: null,
            isDeleted: false,
            vendorId: assignee.vendorId,
          };
          if (_.get(assigneeIdKeyMap, `${assigneeId}.isEquipmentResource`, null)) {
            const operatorId = formAssigneeIds.find((id) => id !== assigneeId);
            obj.operatorId = operatorId;
          }
          newAssigneeIds.push(obj);
          assigneeObj[assignee.id] = assignee;
        });
        const deletedAssigneeIds = _.difference(
          existingAssigneeIds.map((assigneeIdObj) => assigneeIdObj.assigneeId),
          formAssigneeIds
        );
        const deletedAssigneeObjects = deletedAssigneeIds.map((assigneeId) => ({
          assigneeId,
          isDeleted: true,
          sequence: null,
          vendorId: _.get(assigneeIdKeyMap, `${assigneeId}.originalobject.vendorId`, null),
        }));

        task.assigneeIds = _.uniqBy(
          [...newAssigneeIds, ...deletedAssigneeObjects, ...existingDeletedAssigneeIdObjects],
          'assigneeId'
        );
        task.assignee = JSON.stringify(assigneeObj);
        task.assignedOn = dateNow;
      }

      const status = _.get(formValues, 's_status', '') || _.get(formValues, 'status', '');
      if (formValues && status && status !== '') {
        if (task.status !== STATUS_LIST.RESOLVED.key && status === STATUS_LIST.RESOLVED.key) {
          task.resolvedOn = dateNow;
          task.resolvedBy = userName;
          let taskData = parseJSONStringToObject(task.data);
          taskData = { ...taskData, s_resolved_device: RESOLVED_VIA.ADMIN };
          task.data = JSON.stringify(taskData);
        }
        task.status = status;
      }
    }
    return tasksCopy;
  }
  return tasks;
}

export function getOriginalTasks(tasks, selectedTasks) {
  const tasksDict = _.keyBy(tasks, 'id');
  const resTasks = {};
  for (const selectedTask of selectedTasks) {
    resTasks[selectedTask.id] = tasksDict[selectedTask.id];
  }
  return Object.values(resTasks);
}

export function createFilterObjToSave(name, filterModel) {
  return { name, schema: filterModel };
}

export function getGlobalAttributeToColumn(
  globalAttributes = [],
  columnOrder = [],
  columnVisibility = {},
  columnDefs = []
) {
  for (const attribute of globalAttributes || []) {
    let columnObj = {};
    const index = columnOrder.indexOf(attribute.attributeName);
    if (attribute.attributeType === ATTRIBUTE_TYPE.INTEGER.key) {
      columnObj = {
        headerName: attribute.attributeDisplayName,
        field: attribute.attributeName,
        filter: 'agNumberColumnFilter',
        width: 150,
        valueGetter: (params) => _.get(params, `data.globalAttributes.${attribute.attributeName}`),
        hide: !_.get(columnVisibility, `${attribute.attributeName}`),
      };
    } else if (
      attribute.attributeType === ATTRIBUTE_TYPE.STRING.key ||
      attribute.attributeType === ATTRIBUTE_TYPE.DATE.key
    ) {
      columnObj = {
        headerName: attribute.attributeDisplayName,
        field: attribute.attributeName,
        filter: 'agTextColumnFilter',
        width: 150,
        valueGetter: (params) => _.get(params, `data.globalAttributes.${attribute.attributeName}`),
        hide: !_.get(columnVisibility, `${attribute.attributeName}`),
      };
    } else if (
      attribute.attributeType === ATTRIBUTE_TYPE.PICKLIST.key ||
      attribute.attributeType === ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST.key
    ) {
      columnObj = {
        headerName: attribute.attributeDisplayName,
        field: attribute.attributeName,
        filter: 'agTextColumnFilter',
        width: 150,
        valueGetter: (params) => _.get(params, `data.globalAttributes.${attribute.attributeName}.name`),
        hide: !_.get(columnVisibility, `${attribute.attributeName}`),
      };
    }
    if (index > -1) {
      columnDefs.splice(index, 0, columnObj);
    } else {
      columnDefs.push(columnObj);
    }
  }
}

export const permissionCheck = (flagTobeChecked, loggedInUser) => {
  return determineIfUserHasAccessToEngineSetting({
    resourceType: RESOURCE_TYPE.FEATURE,
    resource: RESOURCES.FEATURE.DAILY_SETTINGS_OPTONS,
    resourceTobeChecked: flagTobeChecked,
    user: loggedInUser,
  });
};

export const saveFilterToLocalStorage = (
  { id, name, filterModel, updated = false },
  userConfigUpdateFn,
  userConfigObjKey
) => {
  const saveDataInLocalStorage = !userConfigUpdateFn || !userConfigObjKey;
  const dataToSave = JSON.stringify({ id, name, filterModel, updated });

  if (saveDataInLocalStorage) {
    localStorage.setItem(LOCAL_STORAGE_TASK_LIST_FILTER_MODEL, dataToSave);
  } else {
    userConfigUpdateFn(userConfigObjKey, {
      [LOCAL_STORAGE_TASK_LIST_FILTER_MODEL]: dataToSave,
    });
  }
};

export const saveTempFilterToLocalStorage = (filter) => {
  localStorage.setItem(LOCAL_STORAGE_TASK_LIST_TEMP_FILTER, JSON.stringify(filter));
};
export const getTempFilterToLocalStorage = () => {
  return localStorage.getItem(LOCAL_STORAGE_TASK_LIST_TEMP_FILTER)
    ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_TASK_LIST_TEMP_FILTER))
    : {};
};

export const getFilterfromLocalStorage = () => {
  return localStorage.getItem(LOCAL_STORAGE_TASK_LIST_FILTER_MODEL)
    ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_TASK_LIST_FILTER_MODEL))
    : {};
};

export const removeTempFilterFromLocalStorage = () => {
  localStorage.removeItem(LOCAL_STORAGE_TASK_LIST_TEMP_FILTER);
};

export const removeFilterFromLocalStorage = () => {
  localStorage.removeItem(LOCAL_STORAGE_TASK_LIST_FILTER_MODEL);
};
export const saveSelectedFilterToLocalStorage = ({ id, name, filter, updated = false }) => {
  localStorage.setItem(LOCAL_STORAGE_TASK_LIST_FILTER_MODEL, JSON.stringify({ id, name, filter, updated }));
};

export const overlayCellRenderer = (params) => {
  const emailList = _.get(params, 'data.emailAddressList') ?? [];
  const emailString = emailList.join(', ');
  return (
    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {emailString}
    </span>
  );
};


export const mapPinnedColumns = (pinnedColumnsMap, columnDefs) => {
  const tempCol = columnDefs
    .map((val) => {
      if (_.get(pinnedColumnsMap, 'right', []).includes(val.field)) {
        return { ...val, pinned: 'right' };
      }
      if (_.get(pinnedColumnsMap, 'left', []).includes(val.field)) {
        return { ...val, pinned: 'left' };
      }
      return { ...val };
    })
    .filter((val) => !_.isNil(val));
  return tempCol;
};

export const getAssetLevelColumns = ({ levelsDetails, clonedColumnsMap }) => {
  for (const { name, displayName } of levelsDetails) {
    clonedColumnsMap[name] = {
      headerName: displayName,
      field: name,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => _.get(params, `data.assetLevelsEnrichedInfo.${name}`, ''),
      // hide: !_.get(columnVisibility, `${name}`, false),
      width: 150,
    };
  }
};
